// @flow

import React from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import type { State } from "@store/reducers";

import { split } from "@transformers/schema";
import actionCreators from "@modules/reporting/actionCreator";
import * as selectors from "@modules/reporting/selectors/reports/absence-monitoring";
import * as modalSelectors from "@modules/reporting/selectors/modals";
import { ReportModalStepTypes } from "@modules/reporting/constants/reports";
import type {
  DefaultReport,
  MySavedReport,
  AddEditReportModalType,
  SaveReportModalType,
} from "@modules/reporting/reducer/modals/types";
import type { AbsenceMonitoringReportViewState } from "@modules/reporting/reducer/reports/absence-monitoring/types";
import AddEditReportModal from "../add-edit-report-modal";
import SaveReportModal from "../save-report-modal";
import StepFiltersView from "./step-filters-view";
import config from "@config";

type OwnProps = $Exact<{
  clientId: number,
  returnReportToSave?: (data: MySavedReport) => void,
  getReport?: (data: MySavedReport) => () => void,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  viewState: AbsenceMonitoringReportViewState,
  addEditModal: AddEditReportModalType,
  saveReportModal: SaveReportModalType,
}>;

type PropsFromDispatch = $Exact<{
  getAbsenceMonitoringFiltersSchema: typeof actionCreators.GetAbsenceMonitoringFiltersSchema,
  createAbsenceMonitoringReport: typeof actionCreators.CreateAbsenceMonitoringReport,
  updateAbsenceMonitoringReport: typeof actionCreators.UpdateAbsenceMonitoringReport,
  setReportToSave: typeof actionCreators.SetAbsenceMonitoringReportToSave,
  setViewState: typeof actionCreators.SetAbsenceMonitoringReportViewState,
  toggleAddEditReportModal: typeof actionCreators.ToggleAddEditReportModal,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class AbsenceMonitoringAddEditReportModal extends React.Component<Props> {
  componentDidUpdate(prevProps: Props) {
    if (
      this.props.addEditModal.visible &&
      this.props.addEditModal.visible !== prevProps.addEditModal.visible
    ) {
      this.props.getAbsenceMonitoringFiltersSchema({ clientId: this.props.clientId });
    }
  }

  onUpdateAbsenceSavedReport = (data: MySavedReport) => {
    if (this.props.returnReportToSave) {
      this.props.returnReportToSave(data);
      this.props.toggleAddEditReportModal({ visible: false });
      return;
    }

    this.props.updateAbsenceMonitoringReport({
      clientId: this.props.clientId,
      reportId: data.id,
      data,
    });
    this.props.toggleAddEditReportModal({ visible: false });
  };

  onCreateAbsenceSavedReport = (data: DefaultReport) => {
    this.props.createAbsenceMonitoringReport({
      clientId: this.props.clientId,
      ...data,
      columns: this.props?.columns || data.columns,
    });
  };

  onGetReport = (data: MySavedReport) => {
    if (!this.props.getReport) {
      return;
    }

    this.props.getReport(data)();
    this.props.setReportToSave({
      ...data,
      isNew: true,
    });
    this.props.toggleAddEditReportModal({ visible: false });
  };

  getFiltersView = (data: Object) => {
    return <StepFiltersView data={data} schema={this.props.schema} />;
  };

  collapseFiltersSection = () => {
    this.props.setViewState({ ...this.props.viewState, filtersExpanded: false });
  };

  get modalConfig() {
    return [
      {
        type: ReportModalStepTypes.FILTERS,
        title: {
          new: this.props.t("SET_FILTERS"),
          edit: this.props.t("FILTERS"),
        },
        schema: this.props.schema
          ? split(this.props.schema, [
              "dateRange",
              "fromDate",
              "toDate",
              "monitoringLevels",
              "alertsTriggered",
              "statuses",
              "staffGroups",
            ])
          : {},
        uiSchema: this.stepFiltersUiSchema(this.props.t, config),
        getView: this.getFiltersView,
        hideNextButton: true,
      },
    ];
  }
  get isEditModal(): boolean {
    return !!this.props.addEditModal.data;
  }
  stepFiltersUiSchema = (t: Function, config: Object) => {
    return {
      "ui:order": this.isEditModal
        ? [
            //Period
            "dateRange",
            "fromDate",
            "toDate",
            //Status
            "monitoringLevels",
            "alertsTriggered",
            "statuses",
            //Employee
            "staffGroups",
            "*",
          ]
        : [
            "dateRange",
            "alertsTriggered",
            "fromDate",
            "staffGroups",
            "toDate",
            "statuses",
            "monitoringLevels",
            "*",
          ],
      staffGroups: {
        "ui:header": this.isEditModal && t("EMPLOYEES"),
        "ui:field": "multi-select",
      },
      statuses: {
        "ui:field": "multi-select",
      },
      monitoringLevels: {
        "ui:header": this.isEditModal && t("STATUS"),
        "ui:field": "multi-select",
      },
      alertsTriggered: {
        "ui:field": "multi-select",
      },
      fromDate: {
        "ui:title": t("FROM_DATE"),
        "ui:widget": "date-picker",
        "ui:options": {
          dateFormat: config.dateFormatBE,
        },
      },
      toDate: {
        "ui:title": t("TO_DATE"),
        "ui:widget": "date-picker",
        "ui:options": {
          dateFormat: config.dateFormatBE,
        },
      },
      dateRange: {
        "ui:header": this.isEditModal && t("PERIOD"),
        "ui:options": {
          tooltip: {
            title: t("REPORT_FILTER_DATE_RANGE"),
            description: t("DATE_RANGE_TOOLTIP"),
            id: "monitoring-details-report-date-range",
            size: "medium",
          },
        },
      },
    };
  };

  render() {
    return (
      <React.Fragment>
        {this.props.addEditModal.visible && (
          <AddEditReportModal
            clientId={this.props.clientId}
            initialStep={this.props.addEditModal.initialStep}
            modalConfig={this.modalConfig}
            getReport={this.onGetReport}
            updateReport={this.onUpdateAbsenceSavedReport}
            onClose={this.collapseFiltersSection}
          />
        )}
        {this.props.saveReportModal.visible && (
          <SaveReportModal
            columns={this.props.columns}
            createReport={this.onCreateAbsenceSavedReport}
            updateReport={this.onUpdateAbsenceSavedReport}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  schema: selectors.getAbsenceMonitoringFiltersSchema(state),
  viewState: selectors.getViewState(state),
  addEditModal: modalSelectors.getAddEditReportModal(state),
  saveReportModal: modalSelectors.getSaveReportModal(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  createAbsenceMonitoringReport: actionCreators.CreateAbsenceMonitoringReport,
  updateAbsenceMonitoringReport: actionCreators.UpdateAbsenceMonitoringReport,
  setReportToSave: actionCreators.SetAbsenceMonitoringReportToSave,
  setViewState: actionCreators.SetAbsenceMonitoringReportViewState,
  getAbsenceMonitoringFiltersSchema: actionCreators.GetAbsenceMonitoringFiltersSchema,
  toggleAddEditReportModal: actionCreators.ToggleAddEditReportModal,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  localize(),
)(AbsenceMonitoringAddEditReportModal);
